// Freshness rules for Living Atlas data. "Live" is earned: only observed data
// young enough for its source's real cadence gets it. Forecasts, models,
// historical composites and calculations each keep their own label, and any
// of them turns "stale" once the copy on screen is older than it should be.

import type { AtlasStamp, DataStatus } from "./types";

export interface FreshnessRule {
  /** Observed data no older than this is "live". */
  liveWithinMs: number;
  /** Anything older than this (by source time for observations, by fetch time otherwise) is "stale". */
  staleAfterMs: number;
}

const MIN = 60_000;
const HOUR = 60 * MIN;

/** Per-source rules, from each provider's documented update cadence. */
export const FRESHNESS: Record<string, FreshnessRule> = {
  // Webcam catalogs: which cameras exist changes slowly; each picture has its own age.
  webcams: { liveWithinMs: 0, staleAfterMs: 2 * 24 * HOUR },
  // USGS updates the summary feeds every minute; events are detected within minutes.
  quakes: { liveWithinMs: 30 * MIN, staleAfterMs: 3 * HOUR },
  // OVATION runs every ~5 min with a 30–90 min forecast lead.
  aurora: { liveWithinMs: 20 * MIN, staleAfterMs: 2 * HOUR },
  // Planetary Kp is a 3-hour index.
  kp: { liveWithinMs: 4 * HOUR, staleAfterMs: 12 * HOUR },
  // GIBS GOES frames: 10-min cadence, usually 30–60 min latency.
  goes: { liveWithinMs: 90 * MIN, staleAfterMs: 4 * HOUR },
  // VIIRS overpasses are a few times a day; FIRMS NRT within ~3 h of overpass.
  fires: { liveWithinMs: 6 * HOUR, staleAfterMs: 30 * HOUR },
  incidents: { liveWithinMs: 2 * HOUR, staleAfterMs: 24 * HOUR },
  radar: { liveWithinMs: 20 * MIN, staleAfterMs: HOUR },
  // USGS gauges report every 15 min, transmitted hourly.
  rivers: { liveWithinMs: 2 * HOUR, staleAfterMs: 8 * HOUR },
  // Reservoir telemetry is hourly to daily; storage moves slowly.
  reservoirs: { liveWithinMs: 3 * HOUR, staleAfterMs: 36 * HOUR },
  // SNOTEL daily values: never "live", stale after three days.
  snow: { liveWithinMs: 0, staleAfterMs: 3 * 24 * HOUR },
  // AirNow site AQI is hourly, posted after each hour ends.
  air: { liveWithinMs: 2 * HOUR, staleAfterMs: 5 * HOUR },
  // HMS smoke: hand-drawn from daytime imagery, 1–3 h behind; an analysis, never "live".
  smoke: { liveWithinMs: 0, staleAfterMs: 36 * HOUR },
  // NWS alerts change minute to minute; the national feed is cached 3 min.
  warnings: { liveWithinMs: 15 * MIN, staleAfterMs: HOUR },
  // SPC reports arrive through the day (file rewritten every few minutes)…
  reports: { liveWithinMs: 30 * MIN, staleAfterMs: 3 * HOUR },
  // …and yesterday's is a finished day, recent until it's two days old.
  reportsYesterday: { liveWithinMs: 0, staleAfterMs: 48 * HOUR },
  // NHC advisories every 6 h (3 h intermediates). The advisory is when NHC issued
  // it; the cone is a forecast and is labelled so on the map. Never "live".
  tropical: { liveWithinMs: 0, staleAfterMs: 9 * HOUR },
  // EONET is curated metadata (storm fixes every 6 h, other events daily or less): never "live".
  events: { liveWithinMs: 0, staleAfterMs: 3 * 24 * HOUR },
  // USGS alert levels are the current official status as of our fetch.
  volcanoes: { liveWithinMs: HOUR, staleAfterMs: 12 * HOUR },
  // NOHRSC's national snow analysis is a daily modelled analysis: never "live".
  snowAnalysis: { liveWithinMs: 0, staleAfterMs: 2 * 24 * HOUR },
  // GHRSST MUR is a daily analysis published about a day later: never "live".
  sst: { liveWithinMs: 0, staleAfterMs: 4 * 24 * HOUR },
  // TLE element sets age; CelesTrak refreshes the ISS several times a day.
  tle: { liveWithinMs: 0, staleAfterMs: 3 * 24 * HOUR },
};

export function dataStatus(stamp: AtlasStamp | null | undefined, rule: FreshnessRule, now: number = Date.now()): DataStatus {
  if (!stamp) return "unavailable";
  const fetchedAge = now - Date.parse(stamp.fetchedAt);
  switch (stamp.kind) {
    case "historical":
      return "historical";
    case "calculated":
      return "calculated";
    case "forecast":
    case "model": {
      // A forecast is stale once its valid time has passed by more than the rule allows, or the copy is old.
      const valid = stamp.validTime ? Date.parse(stamp.validTime) : NaN;
      if (Number.isFinite(valid) && now - valid > rule.staleAfterMs) return "stale";
      if (Number.isFinite(fetchedAge) && fetchedAge > rule.staleAfterMs) return "stale";
      return "forecast";
    }
    case "observed": {
      const t = stamp.sourceTime ? Date.parse(stamp.sourceTime) : Date.parse(stamp.fetchedAt);
      if (!Number.isFinite(t)) return "unavailable";
      const age = now - t;
      if (age > rule.staleAfterMs) return "stale";
      // Data served from the Worker's last-good copy is never presented as live.
      if (stamp.servedFromCache) return "recent";
      return age <= rule.liveWithinMs ? "live" : "recent";
    }
  }
}

export const STATUS_LABEL: Record<DataStatus, string> = {
  live: "Live",
  recent: "Recent",
  forecast: "Forecast",
  historical: "Historical",
  calculated: "Calculated",
  stale: "Stale",
  loading: "Loading",
  unavailable: "Unavailable",
};

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function ageText(fromIso: string | null | undefined, now: number = Date.now()): string {
  if (!fromIso) return "time unknown";
  const ms = now - Date.parse(fromIso);
  if (!Number.isFinite(ms)) return "time unknown";
  if (ms < -MIN) return "in the future";
  if (ms < 90_000) return "just now";
  if (ms < HOUR) return `${Math.round(ms / MIN)} min ago`;
  if (ms < 36 * HOUR) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / (24 * HOUR))} days ago`;
}

/**
 * Status of the least-fresh layer that has data, so the top bar never looks
 * better than its worst layer. Layers without data have their own notice.
 * With no data anywhere yet it says Loading while anything is still coming,
 * and Unavailable only once every source has failed.
 */
export function worstStatus(statuses: DataStatus[]): DataStatus {
  const order: DataStatus[] = ["stale", "recent", "forecast", "historical", "calculated", "live"];
  for (const s of order) if (statuses.includes(s)) return s;
  return statuses.length === 0 || statuses.includes("loading") ? "loading" : "unavailable";
}
